import * as THREE from 'three';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';

class DiamondBackground {
    constructor() {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 1, 2000);
        this.renderer = new THREE.WebGLRenderer({
            canvas: document.querySelector('#diamond-bg'),
            alpha: true,
            antialias: true,
            powerPreference: "high-performance"
        });

        // Set white background
        this.renderer.setClearColor(0xffffff, 1);
        this.scene.background = new THREE.Color(0xffffff);

        this.init();
        this.animate();
    }

    init() {
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        // Create diamond material with adjusted transparency settings
        const material = new THREE.MeshPhysicalMaterial({
            color: 0x00ffff,
            metalness: 0.2,
            roughness: 0.0,
            transmission: 0.9,
            thickness: 0.5,
            clearcoat: 1.0,
            clearcoatRoughness: 0.1,
            ior: 2.4,
            reflectivity: 1,
            transparent: true,
            opacity: 0.7, // Reduced opacity for better contrast against white
            side: THREE.DoubleSide,
            depthWrite: true,
            depthTest: true
        });

        // Load the OBJ file
        const loader = new OBJLoader();
        loader.load(
            '/imgs/diamond.obj',
            (object) => {
                object.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        child.material = material;
                        child.geometry.computeVertexNormals();
                        child.geometry.computeBoundingSphere();
                    }
                });

                // Center the diamond
                const box = new THREE.Box3().setFromObject(object);
                const center = box.getCenter(new THREE.Vector3());
                object.position.sub(center);

                // Make the diamond much larger
                const size = box.getSize(new THREE.Vector3());
                const maxDim = Math.max(size.x, size.y, size.z);
                const scale = 100*maxDim; // Increased scale factor
                object.scale.multiplyScalar(scale);

                // Rotate to initial position
                object.rotation.x = Math.PI * 0.1;
                object.rotation.y = Math.PI * 0.25;

                this.diamond = object;
                this.scene.add(this.diamond);
            },
            (xhr) => {
                console.log((xhr.loaded / xhr.total * 100) + '% loaded');
            },
            (error) => {
                console.error('Error loading diamond model:', error);
            }
        );

        // Adjust lighting for white background
        const createDirectionalLight = (x, y, z, intensity) => {
            const light = new THREE.DirectionalLight(0x9000ff, intensity);
            light.position.set(x, y, z);
            return light;
        };

        this.scene.add(createDirectionalLight(5, 5, 5, 1.2));
        this.scene.add(createDirectionalLight(-5, -5, -5, 1));
        this.scene.add(createDirectionalLight(0, 5, 0, 1.1));

        // Sparkle lights with adjusted colors
        const colors = [0x9000ff, 0x7000ff, 0xb000ff];
        for (let i = 0; i < 8; i++) {
            const light = new THREE.PointLight(colors[i % 3], 0.4, 20);
            const angle = (i / 8) * Math.PI * 2;
            const height = i % 2 === 0 ? 4 : -4;
            light.position.set(
                Math.sin(angle) * 8,
                height,
                Math.cos(angle) * 8
            );
            this.scene.add(light);
        }

        // Move camera closer
        this.camera.position.set(6, 3, 6);
        this.camera.lookAt(0, 0, 0);

        window.addEventListener('resize', () => this.onWindowResize());
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    animate() {
        requestAnimationFrame(() => this.animate());

        if (this.diamond) {
            // Slower rotation for a larger object
            this.diamond.rotation.y += 0.003;
            // Gentler wobble
            this.diamond.rotation.x = Math.PI * 0.1 + Math.sin(Date.now() * 0.0008) * 0.08;
        }

        this.renderer.render(this.scene, this.camera);
    }
}

new DiamondBackground();